import { Flex, message } from 'antd';
import { ReactNode, useRef, useState } from 'react';

import { printElement } from '@suntex.core/print';
import { Database_Procs_PrintMembershipCards_Item } from '@suntex.fim/api-client';
import { MembershipCardType } from '@suntex.fim/enums';
import { Button, LoaderOverlay } from '@suntex.fim/ui';

import { MembershipCards } from './MembershipCards';

export interface TeamPrintLoginsProps {
  teamName: string;
  roleLabel: string;
  qrCodeSheetIllustration: ReactNode;
  plainPaperIllustration: ReactNode;
  loadMembershipCards: () => Promise<
    Database_Procs_PrintMembershipCards_Item[]
  >;
}

export function TeamPrintLogins({
  teamName,
  roleLabel,
  qrCodeSheetIllustration,
  plainPaperIllustration,
  loadMembershipCards,
}: TeamPrintLoginsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [cards, setCards] = useState<
    Database_Procs_PrintMembershipCards_Item[]
  >([]);
  const [cardType, setCardType] = useState<MembershipCardType | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const isPrintingRef = useRef(false);

  const handlePrint = async (type: MembershipCardType) => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = true;
    setIsLoading(true);
    try {
      const membershipCards = await loadMembershipCards();
      if (membershipCards.length === 0) {
        message.warning('No student login cards found for this team.');
        return;
      }
      setCards(membershipCards);
      setCardType(type);
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
      if (printRef.current) {
        await printElement(printRef.current);
      }
    } catch (error) {
      let errorMessage = 'An unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
    } finally {
      isPrintingRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <>
      <LoaderOverlay isLoading={isLoading} />
      <Flex vertical gap="20px" className="print-login-style">
        <Flex vertical className="main-navigation">
          <Flex gap="10px" className="main-navigation__heading" align="center">
            <h3>{`${roleLabel} - Print Student Login Labels`}</h3>
          </Flex>
        </Flex>
        <div className="team-print-cards">
          <Flex className="print-card" align="center" gap="20px" vertical>
            {qrCodeSheetIllustration}
            <Flex vertical gap={6} align="center">
              <h5>QR Code Sheet</h5>
              <span>1 Student per page</span>
            </Flex>
            <Flex
              vertical
              gap={10}
              justify="center"
              className="button-container"
            >
              <Button
                shape="round"
                intent="secondary"
                disabled={isLoading}
                onClick={() => {
                  void handlePrint(MembershipCardType.QRCode);
                }}
              >
                Download
              </Button>
              <span className="print-info">Student info (QR Codes)</span>
            </Flex>
          </Flex>
          <Flex className="print-card" align="center" gap="20px" vertical>
            {plainPaperIllustration}
            <Flex vertical gap={6} align="center">
              <h5>Plain Paper (1 page per Student)</h5>
              <span>1 Student per page</span>
            </Flex>
            <Flex
              vertical
              gap={10}
              justify="center"
              className="button-container"
            >
              <Button
                shape="round"
                intent="secondary"
                disabled={isLoading}
                onClick={() => {
                  void handlePrint(MembershipCardType.ParentLetter);
                }}
              >
                Download
              </Button>
              <span className="print-info">
                User ID/Password info onto plain letter-size paper
              </span>
            </Flex>
          </Flex>
        </div>
      </Flex>
      <div ref={printRef} className="team-print-login-cards">
        {cards.length > 0 && cardType !== null && (
          <MembershipCards
            cards={cards}
            cardType={cardType}
            teamName={teamName}
          />
        )}
      </div>
    </>
  );
}
